import { Suspense } from "react";

import { defineMessages } from "react-intl";
import { SoftwareGuardPricingPageProps } from "src/pages/software/guard/pricing/software-guard-pricing-page";

import {
    PriceFinderMessages,
    ProductPlan
} from "@/components/wpl/pricing/src/price-finder/constants";
import PriceFinderSection from "@/components/wpl/pricing/src/price-finder/price-finder";
import { PriceFinderContextProvider } from "@/components/wpl/pricing/src/price-finder/price-finder-context.tsx";
import { createPlanFeatureMessages } from "@/components/wpl/pricing/src/price-finder/utils/create-price-finder-features.tsx";
import { usePricingPlanComparison } from "@/components/wpl/pricing/src/pricing-content-context.tsx";

const GuardPriceFinder = (props: SoftwareGuardPricingPageProps) => {
    const content = {
        standard: usePricingPlanComparison("Standard"),
        premium: usePricingPlanComparison("Premium")
    };

    const standardPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "standard"
    );
    const premiumPlanFeatureMessages = createPlanFeatureMessages(
        content,
        "premium"
    );

    const guardProductPlans: ProductPlan[] = [
        {
            planName: content.standard?.planTitleExternal || "Standard",
            offeringSku: "atlassian-access-standard-sku",
            description: content.standard?.planOneLiner || "",
            recommendationLimit: 0,
            signupLimit: 50_000,
            signupURLInfo: {
                shouldLocalize: false,
                urlDomain: "https://admin.atlassian.com",
                urlPath: "/atlassian-guard"
            },
            planFeatureMessages: standardPlanFeatureMessages
        },
        {
            planName: content.premium?.planTitleExternal || "Premium",
            offeringSku: "atlassian-guard-premium-addon-sku",
            description: content.premium?.planOneLiner || "",
            recommendationLimit: 99,
            signupLimit: 50_000,
            signupURLInfo: {
                shouldLocalize: false,
                urlDomain: "https://admin.atlassian.com",
                urlPath: "/atlassian-guard-premium"
            },
            planFeatureMessages: premiumPlanFeatureMessages
        }
    ];
    const messages: PriceFinderMessages = defineMessages({
        unitCountInputTextRedesign: {
            id: "GuardPriceFinder.priceFinder.unitCountInputTextRedesign",
            defaultMessage: "Team size:"
        },
        unitCountInputTerm: {
            id: "GuardPriceFinder.priceFinder.unitCountInputTerm",
            defaultMessage: "users"
        },
        cardOverSignupLimit: {
            id: "GuardPriceFinder.priceFinder.cardOverSignupLimit",
            defaultMessage: "Your user count exceeds this plan's limit"
        },
        cardEnterpriseMinimumCount: {
            id: "GuardPriceFinder.priceFinder.cardEnterpriseMinimumCount",
            defaultMessage:
                "Enterprise plan pricing is available when you enter {enterpriseMinimumCount} or more users above",
            description:
                "Enterprise is a plan name and should not be translated"
        },
        cardUnitCountTierRange: {
            id: "GuardPriceFinder.priceFinder.cardUnitCountTierRange",
            defaultMessage: "User tier: {lowerLimit} - {upperLimit}"
        },
        cardMonthlyPerUnitCount: {
            id: "GuardPriceFinder.priceFinder.cardMonthlyPerUnitCount",
            defaultMessage: "per user / month"
        }
    });

    return (
        <Suspense>
            <PriceFinderContextProvider
                dataProductKey="atlassianGuard"
                productPlans={guardProductPlans}
                defaultUnitCount={100}
            >
                <PriceFinderSection
                    countryCode={props.countryCode}
                    serverSideCCPPricing={props.pricing?.ccpPricingData?.guard}
                    messages={messages}
                    {...props}
                />
            </PriceFinderContextProvider>
        </Suspense>
    );
};

GuardPriceFinder.displayName = "GuardPriceFinderSection";
export default GuardPriceFinder;
